import React from 'react';
import { Stack, styled, Typography, useTheme } from '@mui/material';
import { format } from 'date-fns';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, StackProps } from '@mui/system';
import { Trip } from 'src/types/trip';
import { Departure } from 'src/services/departures/departures.model';
import { useLocale } from 'src/hooks/useLocale';
import { currencySymbols } from 'src/composites/AllTrips/AllTripsUtil';

interface DepartureCardProps extends StackProps {
  trip: Trip;
  departure: Departure;
}

const DepartureCard: React.FC<DepartureCardProps> = ({ departure, sx }) => {
  const { departureDate, returnDate, remainingSpots } = departure;

  const urgencyLabel = remainingSpots?.label;
  const isPriceDiscounted = departure.originalPrice.amount != departure.finalPrice.amount;
  const discountAmount = departure.originalPrice.amount - departure.finalPrice.amount;
  const discountPercentage = Math.round(
    (discountAmount / departure.originalPrice.amount) * 100,
  );
  const currency = useLocale().currency;
  const discountLabel =
    'Save ' +
    currencySymbols[currency] +
    discountAmount +
    ' (' +
    '-' +
    discountPercentage +
    '%)';
  const urgencyLabelColor =
    remainingSpots?.warningType === 'WARNING_LEVEL_2' ? '#B10C1A' : '#EB8E0C';
  const theme = useTheme();

  return (
    <CardWrapper sx={sx}>
      <Stack direction="row" gap="8px" textAlign="center">
        <DepartureInfo>
          <DateDisplay date={departureDate} />
          <ArrowIcon />
          <DateDisplay date={returnDate} />
        </DepartureInfo>
      </Stack>
      <Box
        display="flex"
        justifyContent={urgencyLabel ? 'space-between' : 'flex-end'}
        alignItems="center"
        width="100%"
      >
        {urgencyLabel && (
          <Typography
            sx={{ color: urgencyLabelColor, fontSize: '12px', lineHeight: '16px' }}
          >
            {urgencyLabel}
          </Typography>
        )}
        {!urgencyLabel && (
          <Typography
            sx={{ color: urgencyLabelColor, fontSize: '12px', lineHeight: '16px' }}
          >
            {departure.remainingSpots?.label}
          </Typography>
        )}
        <Box display="flex" gap={1} alignSelf="end">
          {isPriceDiscounted && (
            <Typography
              variant="Body S"
              sx={{
                color: theme.palette.principal.grey50,
                textDecoration: 'line-through',
              }}
            >
              {departure.originalPrice.label}
            </Typography>
          )}
          <Typography variant="Body S bold">{departure.finalPrice.label}</Typography>
        </Box>
      </Box>
      {isPriceDiscounted && (
        <Box
          sx={{
            width: '100%',
            backgroundColor: theme.palette.brand.crystal,
            display: 'flex',
            justifyContent: 'center',
            borderRadius: '4px',
          }}
        >
          <Typography
            variant="Body S bold"
            sx={{
              color: theme.palette.brand.jungle,
            }}
          >
            {discountLabel}
          </Typography>
        </Box>
      )}
    </CardWrapper>
  );
};

const CardWrapper = styled(Stack)(({ theme }) => ({
  gap: '8px',
  borderRadius: '8px',
  border: `1px solid ${theme.palette.principal.grey50}`,
  padding: '8px',
  backgroundColor: theme.palette.principal.white,
  minWidth: '270px',
}));

const DepartureInfo = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  gap: '4px',
  backgroundColor: theme.palette.principal.grey30,
  borderRadius: '4px',
  flex: 1,
  alignItems: 'center',
  padding: '3px 8px',
  justifyContent: 'space-between',
}));

const ArrowIcon: React.FC = () => (
  <Typography variant="Body S bold" sx={{ display: 'flex', alignItems: 'center' }}>
    <ChevronRightIcon fontSize="small" />
  </Typography>
);

const DateDisplay: React.FC<{ date: Date }> = ({ date }) => {
  const theme = useTheme();
  const formattedDate = format(date, 'd MMM yyyy');
  const dayOfWeek = format(date, 'EEEE');

  return (
    <Stack width="100%" gap="-2px">
      <Typography
        sx={{
          fontSize: '11px',
          lineHeight: '19.8px',
          color: theme.palette.principal.grey70,
        }}
      >
        {dayOfWeek}
      </Typography>
      <Typography variant="body2" sx={{ fontSize: '12px', lineHeight: '14.4px' }}>
        {formattedDate}
      </Typography>
    </Stack>
  );
};

export default DepartureCard;
